import type { Booking, DesignIntent, DesignType, Piece } from '@/domain/types';
import type { NewPiecePayload } from '@/api/intents';

// ---------------------------------------------------------------------------
// A brief's NEW multi-session piece (W5b #5, tv-2na.11.22, live D27). The
// deposit modal only ever CONTINUED an existing piece, so a Messenger brief's
// "New piece · 3 sessions" never attached and the Floor said the booking wasn't
// on a multi-session piece. The piece now rides the hold-slot / create-and-pay
// intents as `newPiece`; the server creates it as `piece-<depositId>` and links
// the booking in the same transaction. These helpers are pure.
// ---------------------------------------------------------------------------

/** The id the server gives a new piece that rides deposit `depositId`. */
export function newPieceIdFor(depositId: string): string {
  return `piece-${depositId}`;
}

/**
 * The `newPiece` payload for a brief, or undefined when the brief is not a NEW
 * piece of two or more sessions (one-off / continue / single session / untitled).
 */
export function newPieceFromBrief(
  intent: Pick<DesignIntent, 'pieceMode' | 'newPieceTitle' | 'estimatedSessions'> | undefined,
  extra: { pieceName?: string; style?: string; placement?: string },
): NewPiecePayload | undefined {
  if (!intent || intent.pieceMode !== 'new') return undefined;
  const sessions = intent.estimatedSessions ?? 0;
  const title = (intent.newPieceTitle ?? extra.pieceName ?? '').trim();
  if (sessions < 2 || !title) return undefined;
  return {
    title,
    estimatedTotalSessions: sessions,
    ...(extra.style ? { style: extra.style } : {}),
    ...(extra.placement ? { placement: extra.placement } : {}),
  };
}

const DESIGN_TYPES: ReadonlySet<string> = new Set<DesignType>([
  'black-and-grey', 'colour-realism', 'traditional', 'neo-traditional', 'fine-line', 'lettering',
  'cover-up', 'blackwork', 'sleeve', 'half-sleeve', 'chest-panel', 'back-piece', 'flash',
]);

/**
 * The piece record the server writes for `newPiece` (apps/api writeIntentPiece),
 * so the store can show it the moment the hold / payment is sent (a
 * server-authored mirror — never PUT by the bridge).
 */
export function newPieceRecord(piece: NewPiecePayload, depositId: string, booking: Booking): Piece {
  const style = piece.style ?? booking.styleTags?.[0] ?? 'custom';
  const designType = (
    piece.designType && DESIGN_TYPES.has(piece.designType) ? piece.designType : DESIGN_TYPES.has(style) ? style : 'blackwork'
  ) as DesignType;
  return {
    id: newPieceIdFor(depositId),
    customerId: booking.customerId,
    title: piece.title,
    designType,
    placement: piece.placement ?? booking.placement ?? booking.bodyPart ?? 'tbc',
    style,
    referencePhotos: booking.referencePhotos ?? [],
    assignedArtistId: booking.artistId,
    estimatedTotalHours: Math.round(((piece.estimatedTotalSessions * (booking.durationMin || 240)) / 60) * 10) / 10,
    hoursCompleted: 0,
    estimatedTotalSessions: piece.estimatedTotalSessions,
    sessionsCompleted: 0,
    status: 'planning',
    bookingsLinked: [booking.id],
    nextPlannedSession: booking.start,
  };
}
